import {
    FlexRowBetween,
    PageHeader,
    AppTable,
    TitleText,
    // DangerText,
} from 'components'
import { useState, useRef, useMemo, memo, useCallback, useEffect } from 'react'
import { ActionPlanService } from 'utility/services/action-plan'
import { Button, Modal, Form, Icon, Tab, List, TextArea, Checkbox, Input, Loader, Dimmer } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { useAuth } from 'context'
import styled from 'styled-components'
import { InspectionService } from 'utility/services/inspection'
import { useActionPlan, GetCustomerList, GetCompanyTemplateList, GetUserList } from 'hooks'
import { useNotification } from 'hooks/toast'
import { DateUtility, DateFormat, DatePickerTypes } from 'utility'
import { MediaUtility } from 'utility/media-utility'
import Webcam from 'react-webcam'
import { X } from 'phosphor-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { AppPrimaryButton, FormDateField, FormDropdownField, SubmitCancelButtons } from 'elements'
import { startOfMonth } from 'date-fns'
import { useParams } from 'react-router-dom'
import { BaseService } from 'utility/services/base'
import { APIPath } from 'utility/constant/api'
import { TicketFileTile } from 'components/TicketFileTile'
import { isImageAttachment } from 'utility/ticket-attachment'

const ActionPlanSchema = yup.object().shape({
    StartDate: yup
        .string()
        .required('Start date required')
        .typeError('Start date required'),
    EndDate: yup
        .string()
        .required('End date required')
        .typeError('End date required'),
    CustomerIds: yup.array().min(0).nullable(),
    TemplateId: yup.string(),
    TicketStatus: yup.array().min(0).nullable(),
    AuditorIds: yup.array().min(0).nullable(),
})

const getInitialTicketStatusName = (ticketStatus = []) => {
    const submitted = ticketStatus.find(
        status =>
            status.TicketStatusId === 1 ||
            String(status.StatusName || '').toLowerCase() === 'submitted',
    )
    return submitted?.StatusName || 'Submitted'
}

const getMediaPreviewUrl = file =>
    MediaUtility.resolvePreviewUrl(file) ||
    file?.FileURL ||
    file?.SignedURL ||
    file?.ImagePathUrl ||
    file?.FilePath ||
    file?.MediaUrl ||
    ''

const getTicketMediaList = plan => {
    if (Array.isArray(plan?.Media) && plan.Media.length) return plan.Media
    if (Array.isArray(plan?.MediaFiles) && plan.MediaFiles.length) {
        return plan.MediaFiles
    }
    return []
}

/** GetByUId may omit Media/Responses; enrich from GetById when logged in. */
const enrichActionPlanDetails = async planData => {
    if (!planData?.ActionPlanId) return planData

    const hasMedia = getTicketMediaList(planData).length > 0
    const hasResponses =
        Array.isArray(planData?.Responses) && planData.Responses.length > 0
    if (hasMedia && hasResponses) return planData

    try {
        const full = await ActionPlanService.getById(planData.ActionPlanId)
        const fullData = full?.data
        if (!fullData) return planData

        return {
            ...planData,
            Media: hasMedia ? planData.Media : fullData.Media,
            MediaIds: planData.MediaIds ?? fullData.MediaIds,
            Responses: hasResponses ? planData.Responses : fullData.Responses,
        }
    } catch {
        return planData
    }
}

const resolveTicketStatusName = (plan, ticketStatus = []) => {
    if (plan?.TicketStatusName) return plan.TicketStatusName
    const fromList = ticketStatus.find(
        status => status.TicketStatusId === plan?.TicketStatus,
    )?.StatusName
    if (fromList) return fromList
    if (Number(plan?.TicketStatus) === 1) return 'Submitted'
    return getInitialTicketStatusName(ticketStatus)
}

const getPreviousResponsesFromTicket = (plan, ticketStatus = []) => {
    const responses = Array.isArray(plan?.Responses) ? plan.Responses : []
    const initialTicket = {
        ResponseId: `ticket-${plan?.ActionPlanId || 'initial'}`,
        ResponseText: plan?.Description || '',
        CreatedOn: plan?.CreatedOn,
        CreatedByUser:
            plan?.CreatedByUser || plan?.CreatedByUserEmail || 'N/A',
        TicketStatusName: resolveTicketStatusName(plan, ticketStatus),
        TicketStatus: plan?.TicketStatus ?? 1,
        Media: getTicketMediaList(plan),
        isInitialTicket: true,
    }

    return [initialTicket, ...responses]
}

const PreviousResponseItem = ({ response, style }) => {
    const isInitialTicket = response.isInitialTicket === true
    const dateValue = isInitialTicket
        ? response.CreatedOn || response.RespondedOn
        : response.RespondedOn
    const byValue = isInitialTicket
        ? response.CreatedByUser || response.RespondedByUserIdName
        : response.RespondedByUserIdName
    const mediaList = Array.isArray(response.Media) ? response.Media : []

    return (
        <List.Item className="response-item" style={style}>
            <div className="response-text">{response.ResponseText}</div>
            {mediaList.length > 0 && (
                <div className="response-media preview-container">
                    {mediaList.map((file, index) => {
                        const src = getMediaPreviewUrl(file)
                        if (!src) return null
                        if (!isImageAttachment(file)) {
                            // Plain fixed-size tile: this list is also shown in the Signature modal, which has
                            // no .preview-box styles.
                            return (
                                <TicketFileTile
                                    key={file.MediaId || `media-${index}`}
                                    url={src}
                                    file={file}
                                    size={80}
                                />
                            )
                        }
                        return (
                            <div
                                key={file.MediaId || `media-${index}`}
                                className="preview-box"
                            >
                                <img
                                    src={src}
                                    alt={`Preview ${index + 1}`}
                                    className="preview-image"
                                />
                            </div>
                        )
                    })}
                </div>
            )}
            <div className="response-meta">
                {isInitialTicket ? 'Created on' : 'Responded on'}:{' '}
                {dateValue
                    ? DateUtility.dateToLocalISO(
                          new Date(dateValue),
                          DateFormat.dateTime,
                      )
                    : 'N/A'}
            </div>
            <div className="response-meta">
                {isInitialTicket ? 'Created by' : 'Responded by'}:{' '}
                {byValue || 'N/A'}
            </div>
            <div className="response-meta">
                Status: {response.TicketStatusName || 'N/A'}
            </div>
        </List.Item>
    )
}

const ActionPlanContainer = styled.div`
    .ui.tabular.menu {
        border-bottom: none;
        margin-bottom: 20px;

        .item {
            border: none !important;
            margin: 0 20px 0 0;
            padding: 10px 0;
            font-weight: 500;
            color: #666;

            &.active {
                color: #2185d0;
                border-bottom: 2px solid #2185d0 !important;
                background: none;
            }

            &:hover {
                background: none;
                color: #2185d0;
            }
        }
    }

    .ui.tab {
        border: none;
        padding: 0;
        margin: 0;
    }

    .ui.table {
        td {
            color: #333;
            font-weight: 500;
        }

        th {
            font-weight: 600 !important;
            color: #222 !important;
        }
    }

    .action-plan-card {
        background: #fff;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .card-header {
        font-size: 18px;
        font-weight: 500;
        margin-bottom: 15px;
    }

    .card-content {
        margin-bottom: 10px;
    }

    .card-label {
        font-weight: 500;
        color: #666;
        margin-bottom: 5px;
    }

    .card-value {
        color: #333;
    }

    .response-button {
        margin-top: 15px;
    }

    .response-history {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #eee;
    }

    .response-item {
        padding: 10px;
        background: #f9f9f9;
        border-radius: 4px;
        margin-bottom: 10px;
    }

    .ticket-data {
        margin-bottom: 20px;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 4px;

        h4 {
            margin-bottom: 15px;
            color: #333;
        }

        .data-row {
            margin-bottom: 10px;

            .label {
                font-weight: 500;
                color: #666;
            }

            .value {
                color: #333;
            }
        }
    }

    .media-buttons {
        display: flex;
        gap: 10px;
        margin-bottom: 15px;
    }

    .preview-container {
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .preview-image {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid #ddd;
    }

    .camera-container {
        margin-top: 15px;

        video {
            width: 100%;
            max-width: 400px;
            border-radius: 4px;
            margin-bottom: 10px;
        }

        canvas {
            display: none;
        }
    }

    .pagination-container {
        margin-top: 20px;
        display: flex;
        justify-content: center;
    }

    .ui.button {
        box-shadow: none !important;
        border: none !important;
    }
`

const CaptureImageModal = styled(Modal)`
    .header {
        background: #f8f9fa !important;
        font-size: 1.2em !important;
        padding: 15px 20px !important;
    }
    .content {
        padding: 20px !important;
        display: flex;
        justify-content: center;
        align-items: center;
        background: #000 !important;
        min-height: 400px;

        video {
            width: 100%;
            max-width: 100%;
            max-height: 70vh;
            object-fit: contain;
        }
    }
    .actions {
        background: #f8f9fa !important;
        padding: 15px 20px !important;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }
`

const ResponseModal = styled(Modal)`
    .content {
        padding: 2rem !important;
        max-height: 80vh !important;
        display: flex !important;
        flex-direction: column !important;
    }

    .modal-scroll-content {
        flex: 1;
        overflow-y: auto;
        padding-right: 10px;
        margin-bottom: 20px;

        &::-webkit-scrollbar {
            width: 8px;
        }
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
        }
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
        }
    }

    .question-section {
        margin-bottom: 20px;
        h4 {
            font-weight: 500;
            color: #333;
            margin-bottom: 10px;
        }
        .question-text {
            color: #666;
        }
    }

    .details-section {
        margin-bottom: 20px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 15px;
        .detail-item {
            .label {
                font-weight: 500;
                color: #333;
                margin-bottom: 5px;
            }
            .value {
                color: #666;
            }
        }
    }

    .media-section {
        margin-bottom: 20px;
        .media-buttons {
            display: flex;
            gap: 10px;
            margin-bottom: 15px;
        }
        .preview-container {
            margin-top: 10px;
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 15px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 8px;
        }
        .preview-box {
            position: relative;
            width: 100%;
            padding-bottom: 100%;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            &:hover {
                transform: scale(1.02);
                transition: transform 0.2s ease;
            }
        }
        .preview-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .previous-responses {
        margin-top: 20px;
        border-top: 1px solid #eee;
        padding-top: 20px;
        position: relative;

        h4 {
            font-weight: 500;
            color: #333;
            margin-bottom: 15px;
            position: sticky;
            top: 0;
            background: white;
            padding: 10px 0;
            z-index: 1;
            border-bottom: 1px solid #eee;
        }

        .responses-list {
            max-height: 360px;
            overflow-y: auto;
            padding-right: 10px;

            &::-webkit-scrollbar {
                width: 8px;
            }
            &::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 4px;
            }
            &::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 4px;
            }
        }

        .response-item {
            padding: 15px;
            background: #f9f9f9;
            border-radius: 6px;
            margin-bottom: 10px;
            .response-text {
                color: #333;
                margin-bottom: 8px;
            }
            .response-meta {
                color: #666;
                font-size: 0.9em;
            }
            .response-media {
                margin: 8px 0 10px;
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
                gap: 10px;
            }
            .preview-box {
                position: relative;
                width: 100%;
                padding-bottom: 100%;
                border-radius: 8px;
                overflow: hidden;
                background: #eee;
            }
            .preview-image {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
    }

    .action-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;
        margin-bottom: 20px;
    }
`

const SignatureModal = styled(Modal)`
    .content {
        padding: 2rem !important;
        max-height: 80vh !important;
        display: flex !important;
        flex-direction: column !important;
    }

    .modal-scroll-content {
        flex: 1;
        overflow-y: auto;
        padding-right: 10px;
        margin-bottom: 20px;
    }

    .signature-pad {
        border: 1px solid #ddd;
        border-radius: 4px;
        margin: 20px 0;
        padding: 20px;
        background: #fff;
    }

    .signature-preview {
        margin-top: 20px;
        padding: 10px;
        border: 1px solid #eee;
        border-radius: 4px;
        min-height: 100px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: 'Allura', cursive;
        font-size: 40px;
    }

    .signature-date {
        margin-top: 10px;
        font-family: 'Arial', sans-serif;
        font-size: 14px;
        color: #666;
    }

    .signature-input {
        margin-bottom: 20px;
    }
`

const ResponseModalContent = memo(
    ({
        showResponseModal,
        selectedPlan,
        responseText,
        setResponseText,
        selectedStatus,
        setSelectedStatus,
        statusOptions,
        showCamera,
        mediaFiles,
        isUploadingMedia = false,
        handleModalClose,
        startCamera,
        stopCamera,
        handleMediaUpload,
        handleResponseSubmit,
        submitting,
        publicScreen,
    }) => {
        const webcamRef = useRef(null)
        const [teamMembers, setTeamMembers] = useState([])
        const [sendForApproval, setSendForApproval] = useState(false)
        const [selectedApprover, setSelectedApprover] = useState(null)

        const fetchTeamMembers = async () => {
            try {
                const response = await BaseService.get(
                    `${APIPath.teamMemberByAuditResponse}${selectedPlan?.AuditId}`,
                )
                const options = response.map(member => ({
                    key: member.unique_id,
                    text: member.Name,
                    value: member.unique_id,
                }))
                setTeamMembers(options)
            } catch (error) {
                console.error('Error fetching team members:', error)
            }
        }

        useEffect(() => {
            if (selectedPlan?.AuditId) {
                fetchTeamMembers()
            }
        }, [selectedPlan?.AuditId])

        const captureImage = useCallback(() => {
            const imageSrc = webcamRef.current.getScreenshot()
            if (imageSrc) {
                fetch(imageSrc)
                    .then(res => res.blob())
                    .then(blob => {
                        const file = new File(
                            [blob],
                            `capture-${Date.now()}.jpg`,
                            {
                                type: 'image/jpeg',
                            },
                        )
                        handleMediaUpload([file])
                    })
                    .catch(err =>
                        console.error('Error processing image capture:', err),
                    )
            }
            stopCamera()
        }, [webcamRef, handleMediaUpload, stopCamera])

        if (!selectedPlan) return null

        return (
            <>
                <ResponseModal
                    open={showResponseModal}
                    onClose={handleModalClose}
                >
                    <Modal.Header>
                        <FlexRowBetween>
                            <TitleText>
                                {publicScreen
                                    ? 'Public Action Plan Response'
                                    : 'Action Plan Response'}
                            </TitleText>
                            <X
                                className="pointer"
                                size={32}
                                onClick={handleModalClose}
                            />
                        </FlexRowBetween>
                    </Modal.Header>
                    <Modal.Content>
                        <div className="modal-scroll-content">
                            <div className="details-section">
                                <div className="question-section">
                                    <label className="font-extrabold">
                                        Question
                                    </label>
                                    <div
                                        className="question-text"
                                        dangerouslySetInnerHTML={{
                                            __html: selectedPlan.Question,
                                        }}
                                    />
                                </div>
                                <div className="detail-item">
                                    <div className="label">
                                        <label className="font-extrabold">
                                            Answer
                                        </label>
                                    </div>
                                    <div className="value">
                                        {selectedPlan.AuditAnswerText || 'N/A'}
                                    </div>
                                </div>
                            </div>
                            <div className="details-section">
                                <div className="detail-item">
                                    <div className="label ">
                                        <label className="font-extrabold">
                                            Audit Name
                                        </label>
                                    </div>
                                    <div className="value">
                                        {selectedPlan.AuditName}
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <div className="label">
                                        <label className="font-extrabold">
                                            Due Date
                                        </label>
                                    </div>
                                    <div className="value">
                                        {DateUtility.dateToLocalISO(
                                            new Date(selectedPlan.DueDate),
                                            DateFormat.date,
                                        )}
                                    </div>
                                </div>
                                {!publicScreen && (
                                    <div className="detail-item">
                                        <div className="label">
                                            <label className="font-extrabold">
                                                Assigned To
                                            </label>
                                        </div>
                                        <div className="value">
                                            {selectedPlan.AssignedUser}
                                        </div>
                                    </div>
                                )}
                                <div className="detail-item">
                                    <div className="label">
                                        <label className="font-extrabold">
                                            Description
                                        </label>
                                    </div>
                                    <div className="value">
                                        {selectedPlan.Description}
                                    </div>
                                </div>
                            </div>
                            <Form>
                                <Form.Field>
                                    <label>Response</label>
                                    <TextArea
                                        placeholder="Enter your response..."
                                        value={responseText}
                                        onChange={e =>
                                            setResponseText(e.target.value)
                                        }
                                        rows={4}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <label>Status</label>
                                    <Dropdown
                                        placeholder="Select Status"
                                        fluid
                                        selection
                                        options={statusOptions}
                                        value={selectedStatus}
                                        onChange={(e, { value }) => {
                                            setSelectedStatus(value)
                                        }}
                                    />
                                </Form.Field>

                                {/* Only show the checkbox if status is 'Resolved' */}
                                {(() => {
                                    const resolvedStatus = statusOptions.find(opt => opt.text === 'Resolved')
                                    if (resolvedStatus && selectedStatus === resolvedStatus.value) {
                                        return (
                                            <>
                                                <Form.Field>
                                                    <Checkbox
                                                        label="Send for approval signature"
                                                        checked={sendForApproval}
                                                        onChange={(e, { checked }) => {
                                                            setSendForApproval(checked)
                                                            if (!checked) {
                                                                setSelectedApprover(null)
                                                            }
                                                        }}
                                                    />
                                                </Form.Field>
                                                {sendForApproval && (
                                                    <Form.Field>
                                                        <label>Select Approver</label>
                                                        <Dropdown
                                                            placeholder="Select Approver"
                                                            fluid
                                                            selection
                                                            options={teamMembers}
                                                            value={selectedApprover}
                                                            onChange={(e, { value }) => {
                                                                setSelectedApprover(value)
                                                            }}
                                                        />
                                                    </Form.Field>
                                                )}
                                            </>
                                        )
                                    }
                                    return null
                                })()}
                                <div className="media-section">
                                    <label className="font-extrabold">
                                        Media
                                    </label>
                                    <div className="media-buttons">
                                        <Button
                                            type="button"
                                            icon
                                            labelPosition="left"
                                            onClick={() =>
                                                document
                                                    .getElementById(
                                                        'response-file-input',
                                                    )
                                                    .click()
                                            }
                                            primary
                                        >
                                            <Icon name="upload" />
                                            Upload File
                                        </Button>
                                        <Button
                                            type="button"
                                            icon
                                            labelPosition="left"
                                            onClick={startCamera}
                                            secondary
                                        >
                                            <Icon name="camera" />
                                            Take Picture
                                        </Button>
                                        <input
                                            id="response-file-input"
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={e =>
                                                handleMediaUpload(
                                                    Array.from(e.target.files),
                                                )
                                            }
                                        />
                                    </div>

                                    {(isUploadingMedia || mediaFiles.length > 0) && (
                                        <div className="preview-container">
                                            {isUploadingMedia && (
                                                <div className="preview-box">
                                                    <Dimmer active inverted>
                                                        <Loader />
                                                    </Dimmer>
                                                </div>
                                            )}
                                            {mediaFiles.map((file, index) =>
                                                !isImageAttachment(file) ? (
                                                    <div
                                                        key={file.MediaId || index}
                                                        className="preview-box"
                                                        style={{ position: 'relative' }}
                                                    >
                                                        <TicketFileTile
                                                            url={
                                                                file.FileURL ||
                                                                file.SignedURL ||
                                                                file.ImagePathUrl
                                                            }
                                                            file={file}
                                                            size={100}
                                                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                                                        />
                                                    </div>
                                                ) : (
                                                <div
                                                    key={file.MediaId || index}
                                                    className="preview-box"
                                                >
                                                    <img
                                                        src={
                                                            file.FileURL ||
                                                            file.SignedURL ||
                                                            file.ImagePathUrl
                                                        }
                                                        alt={`Preview ${
                                                            index + 1
                                                        }`}
                                                        className="preview-image"
                                                    />
                                                </div>
                                                ),
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Form>
                            <div
                                className="action-buttons"
                                style={{
                                    marginTop: '20px',
                                    marginBottom: '20px',
                                }}
                            >
                                <Button onClick={handleModalClose}>
                                    Cancel
                                </Button>
                                <AppPrimaryButton
                                    primary
                                    onClick={() =>
                                        handleResponseSubmit(
                                            selectedApprover,
                                            sendForApproval,
                                        )
                                    }
                                    loading={submitting}
                                >
                                    Submit Response
                                </AppPrimaryButton>
                            </div>
                            {selectedPlan.previousResponses &&
                                selectedPlan.previousResponses.length > 0 && (
                                    <div className="previous-responses">
                                        <h4>
                                            <label className="text-lg font-extrabold">
                                                Previous Responses
                                            </label>
                                        </h4>
                                        <div className="responses-list">
                                            <List>
                                                {selectedPlan.previousResponses.map(
                                                    response => (
                                                        <PreviousResponseItem
                                                            key={
                                                                response.ResponseId
                                                            }
                                                            response={response}
                                                        />
                                                    ),
                                                )}
                                            </List>
                                        </div>
                                    </div>
                                )}
                        </div>
                    </Modal.Content>
                </ResponseModal>

                <CaptureImageModal
                    open={showCamera}
                    onClose={stopCamera}
                    size="small"
                >
                    <Modal.Header>
                        <FlexRowBetween>
                            <TitleText>Capture Image</TitleText>
                            <X
                                className="pointer"
                                size={32}
                                onClick={stopCamera}
                            />
                        </FlexRowBetween>
                    </Modal.Header>
                    <Modal.Content>
                        <Webcam
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            style={{
                                width: '100%',
                                height: 'auto',
                                maxWidth: '100vw',
                            }}
                            className="responsive-webcam"
                        />
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={stopCamera}>Cancel</Button>
                        <Button color="green" onClick={captureImage}>
                            Capture
                        </Button>
                    </Modal.Actions>
                </CaptureImageModal>
            </>
        )
    },
)

const SignatureModalContent = memo(
    ({
        showSignatureModal,
        selectedPlan,
        handleModalClose,
        handleSignatureSubmit,
        submitting,
    }) => {
        const [signatureName, setSignatureName] = useState('')
        const [signaturePreview, setSignaturePreview] = useState('')
        const canvasRef = useRef(null)
        const today = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })

        useEffect(() => {
            if (signatureName) {
                setSignaturePreview(signatureName)
            }
        }, [signatureName])

        // Draw the signature name on the canvas in signature font
        const getSignatureBase64 = () => {
            const canvas = canvasRef.current
            if (!canvas) return ''
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.font = "32px 'Dancing Script', cursive"
            ctx.fillStyle = '#222'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.fillText(signatureName, 10, canvas.height / 2)
            return canvas.toDataURL('image/png')
        }

        // Wrap the submit to use the base64 signature
        const handleSubmitSignature = () => {
            const signatureBase64 = getSignatureBase64()
            handleSignatureSubmit(signatureName, signatureBase64)
        }

        if (!selectedPlan) return null

        return (
            <SignatureModal
                open={showSignatureModal}
                onClose={handleModalClose}
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>Digital Signature</TitleText>
                        <X
                            className="pointer"
                            size={32}
                            onClick={handleModalClose}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <div className="modal-scroll-content">
                        <div className="details-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                            <div className="question-section">
                                <label className="font-extrabold">Question</label>
                                <div className="question-text" dangerouslySetInnerHTML={{ __html: selectedPlan.Question }} />
                            </div>
                            <div className="detail-item">
                                <div className="label"><label className="font-extrabold">Answer</label></div>
                                <div className="value">{selectedPlan.AuditAnswerText || 'N/A'}</div>
                            </div>
                        </div>
                        <div className="details-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                            <div className="detail-item">
                                <div className="label "><label className="font-extrabold">Audit Name</label></div>
                                <div className="value">{selectedPlan.AuditName}</div>
                            </div>
                            <div className="detail-item">
                                <div className="label"><label className="font-extrabold">Due Date</label></div>
                                <div className="value">{DateUtility.dateToLocalISO(new Date(selectedPlan.DueDate), DateFormat.date)}</div>
                            </div>
                            <div className="detail-item" style={{ gridColumn: '1 / span 2' }}>
                                <div className="label"><label className="font-extrabold">Description</label></div>
                                <div className="value">{selectedPlan.Description}</div>
                            </div>
                        </div>
                        <Form>
                            <Form.Field className="signature-input">
                                <label>Type Your Name</label>
                                <Input
                                    placeholder="Enter your name..."
                                    value={signatureName}
                                    onChange={(e, { value }) => setSignatureName(value)}
                                />
                            </Form.Field>
                            <div className="signature-pad">
                                <label className="font-extrabold">Your Signature</label>
                                <div className="signature-preview">
                                    {signaturePreview}
                                    <div className="signature-date">{today}</div>
                                </div>
                            </div>
                            {/* Hidden canvas for signature image generation */}
                            <canvas ref={canvasRef} width={400} height={60} style={{ display: 'none' }} />
                        </Form>
                        <div className="action-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, marginBottom: 20 }}>
                            <Button onClick={handleModalClose}>Cancel</Button>
                            <Button
                                primary
                                onClick={handleSubmitSignature}
                                loading={submitting}
                                disabled={!signatureName.trim()}
                            >
                                Submit Signature
                            </Button>
                        </div>
                        {selectedPlan.previousResponses && selectedPlan.previousResponses.length > 0 && (
                            <div className="previous-responses" style={{ marginTop: 30, borderTop: '1px solid #eee', paddingTop: 20 }}>
                                <h4>
                                    <label className="text-lg font-extrabold" style={{ fontSize: 20, fontWeight: 700, marginBottom: 15, display: 'block' }}>
                                        Previous Responses
                                    </label>
                                </h4>
                                <div className="responses-list" style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 10 }}>
                                    <List>
                                        {selectedPlan.previousResponses.map(response => (
                                            <PreviousResponseItem
                                                key={response.ResponseId}
                                                response={response}
                                                style={{ padding: 15, background: '#f9f9f9', borderRadius: 6, marginBottom: 10 }}
                                            />
                                        ))}
                                    </List>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Content>
            </SignatureModal>
        )
    },
)

export const ActionPlanList = ({ publicScreen }) => {
    const { errorToast: uploadErrorToast } = useNotification()
    const { user } = useAuth()
    const [showResponseModal, setShowResponseModal] = useState(false)
    const [showSignatureModal, setShowSignatureModal] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState(null)
    const [responseText, setResponseText] = useState('')
    const [showCamera, setShowCamera] = useState(false)
    const [mediaFiles, setMediaFiles] = useState([])
    const [isUploadingMedia, setIsUploadingMedia] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize] = useState(20)
    const [activeTab, setActiveTab] = useState(0)
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submittingSignature, setSubmittingSignature] = useState(false)
    const [selectedStatus, setSelectedStatus] = useState(null)
    const [filterParams, setFilterParams] = useState(null)
    const [lastAssignedFilters, setLastAssignedFilters] = useState(null);
    const [lastMyTicketsFilters, setLastMyTicketsFilters] = useState(null);
    const { id } = useParams()

    const defaultStartDate = startOfMonth(new Date())

    const { options: customerList } = GetCustomerList()
    const { options: auditorOptions } = GetUserList()
    const { options: templateOptions } = GetCompanyTemplateList()

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        getValues,
    } = useForm({
        resolver: yupResolver(ActionPlanSchema),
        defaultValues: {
            CustomerIds: [],
            AuditorIds: [],
            StartDate: defaultStartDate,
            EndDate: new Date(),
            TemplateId: '',
            TicketStatus: [],
        },
    })

    const {
        assignedTickets,
        myTickets,
        fetchAssignedTickets,
        fetchMyTickets,
        ticketStatus,
        setAssignedTickets,
        setMyTickets,
    } = useActionPlan(user?.unique_id)

    const statusOptions = useMemo(() => {
        return ticketStatus.map(status => ({
            key: status.TicketStatusId,
            text: status.StatusName,
            value: status.TicketStatusId,
        }))
    }, [ticketStatus])

    const fetchData = useCallback(
        (page = currentPage, filters = filterParams) => {
            setLoading(true)
            if (activeTab === 0) {
                fetchAssignedTickets(page, pageSize, filters)
            } else {
                fetchMyTickets(page, pageSize, {
                    ...filters,
                    pageNumber: page,
                    pageSize,
                    startDate: filters?.startDate || '',
                    endDate: filters?.endDate || '',
                    customerIds: filters?.customerIds || '',
                    templateIds: filters?.templateIds || '',
                    ticketStatusIds: filters?.ticketStatusIds || '',
                })
            }
            setLoading(false)
        },
        [
            activeTab,
            pageSize,
            fetchAssignedTickets,
            fetchMyTickets,
            currentPage,
            filterParams,
        ],
    )

    const formatDateForAPI = (dateStr, isEndDate = false) => {
        if (!dateStr) return ''
        const date = new Date(dateStr)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}${isEndDate ? 'T23:59:59' : 'T00:00:00'}`
    }

    // Helper to get formatted date range for the message for a given filter
    const getNoDataMessageForTab = (filters) => {
        if (!filters) return 'No data found';
        const { startDate, endDate } = filters;
        if (startDate && endDate) {
            const start = DateUtility.dateToLocalISO(new Date(startDate), DateFormat.date);
            const end = DateUtility.dateToLocalISO(new Date(endDate), DateFormat.date);
            return `No data available between ${start} to ${end}, please try with a different date`;
        }
        return 'No data found';
    };

    const submit = async formData => {
        if (!formData.StartDate || !formData.EndDate) {
            return
        }

        // Format dates for API with correct end time
        const startDate = formatDateForAPI(formData.StartDate, false)
        const endDate = formatDateForAPI(formData.EndDate, true)

        // Prepare filter parameters
        const params = {
            startDate,
            endDate,
            customerIds: formData.CustomerIds?.length
                ? formData.CustomerIds.join(';')
                : '',
            templateIds: formData.TemplateId || '',
            ticketStatusIds: formData.TicketStatus?.length
                ? formData.TicketStatus.join(';')
                : '',
            auditorIds: formData.AuditorIds?.length
                ? formData.AuditorIds.join(';')
                : '',
        }

        setFilterParams(params)
        setCurrentPage(1)

        if (activeTab === 0) {
            setLastAssignedFilters(params);
            await fetchAssignedTickets(1, pageSize, params)
        } else {
            setLastMyTicketsFilters(params);
            await fetchMyTickets(1, pageSize, params)
        }
    }

    const handleTabChange = (_, { activeIndex }) => {
        setActiveTab(activeIndex)
        setCurrentPage(1)

        // Clear the data without making an API call
        if (activeIndex === 0) {
            setAssignedTickets({ items: [], totalCount: 0 })
        } else {
            setMyTickets({ items: [], totalCount: 0 })
        }
    }

    const resetForm = () => {
        reset({
            CustomerIds: [],
            AuditorIds: [],
            StartDate: defaultStartDate,
            EndDate: new Date(),
            TemplateId: '',
            TicketStatus: [],
        })
        setFilterParams(null)
        setCurrentPage(1)

        // Clear the data without making an API call
        if (activeTab === 0) {
            setAssignedTickets({ items: [], totalCount: 0 })
        } else {
            setMyTickets({ items: [], totalCount: 0 })
        }
    }

    const handlePageChange = async page => {
        setCurrentPage(page)
        const currentFilters = getValues()
        const params = {
            startDate: formatDateForAPI(currentFilters.StartDate, false),
            endDate: formatDateForAPI(currentFilters.EndDate, true),
            customerIds: currentFilters.CustomerIds?.length
                ? currentFilters.CustomerIds.join(';')
                : '',
            templateIds: currentFilters.TemplateId || '',
            ticketStatusIds: currentFilters.TicketStatus?.length
                ? currentFilters.TicketStatus.join(';')
                : '',
            auditorIds: currentFilters.AuditorIds?.length
                ? currentFilters.AuditorIds.join(';')
                : '',
        }
        fetchData(page, params)
    }

    const startCamera = () => {
        setShowCamera(true)
    }

    const stopCamera = () => {
        setShowCamera(false)
    }

    const handleMediaUpload = async files => {
        if (!files?.length) return
        setIsUploadingMedia(true)
        try {
            const uploadedMediaFiles = await Promise.all(
                files.map(async file => {
                    const uploadResponse = await InspectionService.uploadMedia({
                        media: file,
                    })

                    const savedMedia = await InspectionService.saveMedia([
                        {
                            MediaId: 0,
                            FAQId: selectedPlan.ActionPlanId,
                            Order: 1,
                            FolderName: '1',
                            FileType: file.type || 'application/octet-stream',
                            FileName: (file.name || '').slice(0, 200),
                            SignedURL:
                                uploadResponse.ImagePathUrl ||
                                uploadResponse.FileURL,
                            FileURL:
                                uploadResponse.ImagePathUrl ||
                                uploadResponse.FileURL,
                            FileKey:
                                uploadResponse.CloudImageId ||
                                uploadResponse.FileKey,
                            MediaSourceType: 3,
                        },
                    ])

                    // keep the type / name for the preview (the saved row may not echo them)
                    const saved = savedMedia[0] || {}
                    return {
                        ...saved,
                        FileType: saved.FileType || file.type || 'application/octet-stream',
                        FileName: saved.FileName || file.name || '',
                    }
                }),
            )

            setMediaFiles(prev => [...prev, ...uploadedMediaFiles])
        } catch (error) {
            console.error('Error uploading media:', error)
            uploadErrorToast('The file could not be uploaded. Please try again.')
        } finally {
            setIsUploadingMedia(false)
        }
    }

    const handleResponseClick = async item => {
        try {
            setLoading(true)
            const response = await ActionPlanService.getById(item.ActionPlanId)
            if (response?.data) {
                setSelectedPlan({
                    ...response.data,
                    previousResponses: getPreviousResponsesFromTicket(
                        response.data,
                        ticketStatus,
                    ),
                })
                setSelectedStatus(response.data.TicketStatus)
                setResponseText('')
                setShowResponseModal(true)
            }
        } catch (error) {
            console.error('Error fetching action plan details:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleModalClose = () => {
        stopCamera()
        setShowResponseModal(false)
        setSelectedPlan(null)
        setResponseText('')
        setMediaFiles([])
        setIsUploadingMedia(false)
        setSelectedStatus(null)
    }

    const handleResponseSubmit = async (selectedApprover, sendForApproval) => {
        if (!responseText.trim()) return
        try {
            setSubmitting(true)
            const responseData = {
                ActionPlanId: selectedPlan.ActionPlanId,
                RespondedByUserId: user.unique_id,
                ResponseText: responseText,
                ResponseAccepted: true,
                TicketStatus: selectedStatus,
                ApproverUserId: selectedApprover,
                IsSendSignature: sendForApproval,
                SignatureById: selectedApprover,
            }
            const responseId = await ActionPlanService.createResponse(
                responseData,
            )
            if (mediaFiles.length > 0) {
                try {
                    const mediaIds = mediaFiles
                        .map(file => file.MediaId)
                        .filter(Boolean)
                    if (mediaIds.length > 0) {
                        await ActionPlanService.updateMediaReferences(
                            mediaIds,
                            responseId,
                        )
                    }
                } catch (mediaError) {
                    console.error(
                        'Error attaching media to response:',
                        mediaError,
                    )
                }
            }
            handleModalClose()
            fetchData()
        } catch (error) {
            console.error('Error submitting response:', error)
        } finally {
            setSubmitting(false)
        }
    }

    const handleSignatureModalClose = () => {
        setShowSignatureModal(false)
        setSelectedPlan(null)
    }

    const handleSignatureSubmit = async (signatureName, signatureBase64) => {
        if (!signatureName.trim()) return
        try {
            setSubmittingSignature(true)
            const today = new Date().toISOString()
            const signatureData = {
                ActionPlanId: selectedPlan.ActionPlanId,
                Signature: signatureBase64,
                CreatedOn: today,
                CreatedBy: user.unique_id,
                SignatureName: signatureName,
                SignatureById: user.unique_id,
                SignatureByEmail: user.Email,
            }
            await ActionPlanService.updateSignature(signatureData)
            handleSignatureModalClose()
            fetchData()
        } catch (error) {
            console.error('Error submitting signature:', error)
        } finally {
            setSubmittingSignature(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                title: 'Audit Name',
                key: 'AuditName',
                render: item => (
                    <span style={{ fontWeight: 500, color: '#333' }}>
                        {item.AuditName}
                    </span>
                ),
            },
            {
                title: 'Description',
                key: 'Description',
                render: item => (
                    <span style={{ fontWeight: 500, color: '#333' }}>
                        {item.Description}
                    </span>
                ),
            },
            {
                title: 'Due Date',
                render: item => (
                    <span style={{ fontWeight: 500, color: '#333' }}>
                        {DateUtility.dateToLocalISO(
                            new Date(item.DueDate),
                            DateFormat.date,
                        )}
                    </span>
                ),
            },
            {
                title: 'Status',
                render: item => (
                    <span style={{ fontWeight: 500, color: '#333' }}>
                        {ticketStatus.find(
                            s => s.TicketStatusId === item.TicketStatus,
                        )?.StatusName || item.TicketStatus}
                    </span>
                ),
            },
            {
                title: 'Actions',
                render: item => (
                    <AppPrimaryButton onClick={() => handleResponseClick(item)} size="small">
                        Response
                    </AppPrimaryButton>
                ),
            },
        ],
        [ticketStatus],
    )

    const panes = useMemo(
        () => [
            {
                menuItem: 'Assigned Tickets',
                render: () => (
                    <Tab.Pane>
                        <AppTable
                            data={assignedTickets.items || []}
                            columns={columns}
                            loading={loading}
                            pageChanged={handlePageChange}
                            filter={{
                                PageNumber: currentPage,
                                PageSize: pageSize,
                            }}
                            totalCount={assignedTickets.totalCount}
                            emptyMessage={getNoDataMessageForTab(lastAssignedFilters)}
                        />
                    </Tab.Pane>
                ),
            },
            {
                menuItem: 'My Tickets',
                render: () => (
                    <Tab.Pane>
                        <AppTable
                            data={myTickets.items || []}
                            columns={columns}
                            loading={loading}
                            pageChanged={handlePageChange}
                            filter={{
                                PageNumber: currentPage,
                                PageSize: pageSize,
                            }}
                            totalCount={myTickets.totalCount}
                            emptyMessage={getNoDataMessageForTab(lastMyTicketsFilters)}
                        />
                    </Tab.Pane>
                ),
            },
        ],
        [
            assignedTickets,
            myTickets,
            columns,
            loading,
            currentPage,
            pageSize,
            handlePageChange,
            lastAssignedFilters,
            lastMyTicketsFilters,
        ],
    )

    // Add useEffect to fetch data on component mount
    useEffect(() => {
        if (user?.unique_id) {
            const initialParams = {
                startDate: formatDateForAPI(defaultStartDate, false),
                endDate: formatDateForAPI(new Date(), true),
                customerIds: '',
                templateIds: '',
                ticketStatusIds: '',
            }
            setFilterParams(initialParams)
        }
    }, [user?.unique_id])

    // If publicScreen is true, we'll fetch a single action plan
    useEffect(() => {
        if (publicScreen && id) {
            const fetchPublicActionPlan = async () => {
                try {
                    setLoading(true)
                    // If not logged in, redirect to sign-in page with return URL
                    if (!user?.unique_id) {
                        const returnPath = window.location.pathname
                        window.location.href = `/sign-in?returnUrl=${encodeURIComponent(
                            returnPath,
                        )}`
                        return
                    }

                    let response
                    if (window.location.pathname.includes('/Signature')) {
                        response = await ActionPlanService.getSignature(id)
                    } else {
                        response = await ActionPlanService.getByUId(id)
                    }
                    if (response?.data) {
                        let planData = response.data
                        if (!window.location.pathname.includes('/Signature')) {
                            planData = await enrichActionPlanDetails(planData)
                        }
                        setSelectedPlan({
                            ...planData,
                            previousResponses: getPreviousResponsesFromTicket(
                                planData,
                                ticketStatus,
                            ),
                        })
                        if (window.location.pathname.includes('/Signature')) {
                            setShowSignatureModal(true)
                        } else {
                            setSelectedStatus(response.data.TicketStatus)
                            setShowResponseModal(true)
                        }
                    }
                } catch (error) {
                    console.error('Error fetching public action plan:', error)
                } finally {
                    setLoading(false)
                }
            }
            fetchPublicActionPlan()
        }
    }, [publicScreen, id, user])

    useEffect(() => {
        if (!publicScreen || !selectedPlan?.ActionPlanId) return
        setSelectedPlan(prev => {
            if (!prev) return prev
            return {
                ...prev,
                previousResponses: getPreviousResponsesFromTicket(
                    prev,
                    ticketStatus,
                ),
            }
        })
    }, [ticketStatus, publicScreen])

    // If it's a public screen, we only show the response modal or signature modal
    if (publicScreen) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 999999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    pointerEvents: 'none',
                }}
            >
                <div
                    style={{
                        pointerEvents: 'auto',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {showResponseModal && (
                        <ResponseModalContent
                            showResponseModal={showResponseModal}
                            selectedPlan={selectedPlan}
                            responseText={responseText}
                            setResponseText={setResponseText}
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            statusOptions={statusOptions}
                            showCamera={showCamera}
                            mediaFiles={mediaFiles}
                            isUploadingMedia={isUploadingMedia}
                            handleModalClose={handleModalClose}
                            startCamera={startCamera}
                            stopCamera={stopCamera}
                            handleMediaUpload={handleMediaUpload}
                            handleResponseSubmit={handleResponseSubmit}
                            submitting={submitting}
                            publicScreen={publicScreen}
                        />
                    )}
                    {showSignatureModal && (
                        <SignatureModalContent
                            showSignatureModal={showSignatureModal}
                            selectedPlan={selectedPlan}
                            handleModalClose={handleSignatureModalClose}
                            handleSignatureSubmit={handleSignatureSubmit}
                            submitting={submittingSignature}
                        />
                    )}
                </div>
            </div>
        )
    }

    return (
        <ActionPlanContainer>
            <FlexRowBetween>
                <PageHeader header="Action Plans" />
            </FlexRowBetween>

            <Form onSubmit={handleSubmit(submit)} className="mb-4">
                <Form.Group widths="equal">
                    <FormDateField
                        name="StartDate"
                        control={control}
                        label="Start Date"
                        type={DatePickerTypes.Single}
                        placeholder="Select start date"
                        errors={errors.StartDate}
                    />
                    <FormDateField
                        name="EndDate"
                        control={control}
                        label="End Date"
                        type={DatePickerTypes.Single}
                        placeholder="Select end date"
                        errors={errors.EndDate}
                    />
                    {(user?.CompanyId === 34930 ||
                        user?.CompanyId === 60551) && (
                        <FormDropdownField
                            name="AuditorIds"
                            control={control}
                            label="Auditor"
                            placeholder="Select auditor"
                            options={auditorOptions}
                            errors={errors.AuditorIds}
                            multiple
                        />
                    )}
                </Form.Group>
                <Form.Group widths="equal">
                    <FormDropdownField
                        name="CustomerIds"
                        control={control}
                        label="Customer"
                        placeholder="Select customer"
                        options={customerList}
                        errors={errors.CustomerIds}
                        multiple
                    />
                    <FormDropdownField
                        name="TemplateId"
                        control={control}
                        label="Template"
                        placeholder="Select template"
                        options={templateOptions}
                        errors={errors.TemplateId}
                    />

                    <FormDropdownField
                        name="TicketStatus"
                        control={control}
                        label="Status"
                        placeholder="Select status"
                        options={statusOptions}
                        errors={errors.TicketStatus}
                        multiple
                    />
                </Form.Group>
                <div className="row mt-3">
                    <div className="col-12">
                        <SubmitCancelButtons
                            onCancel={resetForm}
                            cancelText="Reset"
                            submitText="Submit"
                        />
                    </div>
                </div>
            </Form>

            <Tab
                panes={panes}
                onTabChange={handleTabChange}
                activeIndex={activeTab}
            />

            <ResponseModalContent
                showResponseModal={showResponseModal}
                selectedPlan={selectedPlan}
                responseText={responseText}
                setResponseText={setResponseText}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                statusOptions={statusOptions}
                showCamera={showCamera}
                mediaFiles={mediaFiles}
                isUploadingMedia={isUploadingMedia}
                handleModalClose={handleModalClose}
                startCamera={startCamera}
                stopCamera={stopCamera}
                handleMediaUpload={handleMediaUpload}
                handleResponseSubmit={handleResponseSubmit}
                submitting={submitting}
                publicScreen={publicScreen}
            />

            <SignatureModalContent
                showSignatureModal={showSignatureModal}
                selectedPlan={selectedPlan}
                handleModalClose={handleSignatureModalClose}
                handleSignatureSubmit={handleSignatureSubmit}
                submitting={submittingSignature}
            />
        </ActionPlanContainer>
    )
}
